import { useMemo, useState } from 'react';
import type { LayerOpacityState, LayerVisibilityState } from '../utils/MapLayerManager';
import { buildCaviteSilangPreviewUrl } from '../constants/caviteSilangLayers';
import { buildIsabelaPhilricePreviewUrl } from '../constants/isabelaPhilriceLayers';
import {
  DRONE_MISSIONS,
  type DroneMissionConfig,
  type DroneMissionKey,
} from '../constants/dronePrescriptionLayers';
import { isMapLayerAllowed } from '../../../config/analysisLayerEntitlements';
import DroneServiceRequestModal from './DroneServiceRequestModal';
import FarmDroneOrthoAnalysis from './FarmDroneOrthoAnalysis';
import type { DroneServiceType } from '../../../services/droneServiceService';

type DroneMapMode = 'with' | 'without';

interface FarmDroneDataPanelProps {
  isDark: boolean;
  isCompact?: boolean;
  selectedField?: { farm_name?: string; farm_id?: string | number } | null;
  /** Selected field geometry — used for overlap check + orthophoto analysis. */
  fieldGeoJson?: Record<string, unknown> | null;
  layerVisibility: LayerVisibilityState;
  layerOpacity: LayerOpacityState;
  onLayerToggle: (key: keyof LayerVisibilityState, visible: boolean) => void;
  onOpacityChange: (key: keyof LayerOpacityState, opacity: number) => void;
  onRequestDronePrescriptionStats?: (mission: DroneMissionConfig) => void;
  onClearDronePrescriptionStats?: () => void;
  onShowDroneOrthoIndexMap?: (mission: DroneMissionConfig, tileUrl: string) => void;
  onClearDroneOrthoIndexMap?: () => void;
  allowedEntitlementSet: Set<string> | null;
}

const SAMPLE_OPTIONS: { key: DroneMissionKey; label: string; icon: string }[] = [
  { key: 'isabela', label: 'Isabela_Philrice', icon: 'ri-stack-line' },
  { key: 'cavite', label: 'Cavite_Silang', icon: 'ri-map-pin-user-line' },
];

export default function FarmDroneDataPanel({
  isDark,
  isCompact = false,
  selectedField,
  fieldGeoJson = null,
  layerVisibility,
  layerOpacity,
  onLayerToggle,
  onOpacityChange,
  onRequestDronePrescriptionStats,
  onClearDronePrescriptionStats,
  onShowDroneOrthoIndexMap,
  onClearDroneOrthoIndexMap,
  allowedEntitlementSet,
}: FarmDroneDataPanelProps) {
  const canShow = (k: keyof LayerVisibilityState) => isMapLayerAllowed(allowedEntitlementSet, k);

  const [droneMapMode, setDroneMapMode] = useState<DroneMapMode>('without');
  const [selectedSample, setSelectedSample] = useState<DroneMissionKey>('isabela');
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [droneServiceType, setDroneServiceType] = useState<DroneServiceType | null>(null);

  const activeMission = useMemo(
    () => DRONE_MISSIONS.find((m) => m.key === selectedSample) ?? DRONE_MISSIONS[0],
    [selectedSample]
  );

  const isabelaVisible =
    layerVisibility.isabelaPhilriceNDVI || layerVisibility.isabelaPhilriceOrtho;
  const caviteVisible = layerVisibility.caviteSilangNDVI || layerVisibility.caviteSilangOrtho;
  const hasActiveDroneLayer = isabelaVisible || caviteVisible;

  const canShowIsabela =
    canShow('isabelaPhilriceNDVI') || canShow('isabelaPhilriceOrtho');
  const canShowCavite = canShow('caviteSilangNDVI') || canShow('caviteSilangOrtho');

  const textXs = isCompact ? 'text-[10px]' : 'text-xs';
  const textSm = isCompact ? 'text-[11px]' : 'text-sm';
  const pad = isCompact ? 'p-2' : 'p-3';
  const gap = isCompact ? 'gap-1.5' : 'gap-2';

  const toggleNdvi = (mission: DroneMissionConfig) => {
    const key = mission.ndviLayerKey;
    if (layerVisibility[key]) {
      onLayerToggle(key, false);
      onClearDronePrescriptionStats?.();
    } else {
      onLayerToggle(key, true);
      onRequestDronePrescriptionStats?.(mission);
    }
  };

  const toggleOrtho = (mission: DroneMissionConfig, orthoKey: 'isabelaPhilriceOrtho' | 'caviteSilangOrtho') => {
    onLayerToggle(orthoKey, !layerVisibility[orthoKey]);
  };

  const renderLayerCard = (
    mission: DroneMissionConfig,
    type: 'ndvi' | 'ortho',
    layerKey: keyof LayerVisibilityState,
    label: string,
    icon: string,
    previewUrl: string,
    onClick: () => void
  ) => {
    const active = layerVisibility[layerKey];
    return (
      <div
        key={layerKey}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        className={`rounded-lg border-2 transition-all cursor-pointer ${
          isCompact ? 'p-1.5' : 'p-2'
        } ${
          active
            ? isDark
              ? 'border-cyan-400 bg-cyan-900/30'
              : 'border-cyan-500 bg-cyan-50'
            : isDark
              ? 'border-gray-600 bg-gray-800 hover:border-cyan-500/50'
              : 'border-gray-200 bg-white hover:border-cyan-300'
        }`}
      >
        <div className="flex items-start gap-2 pointer-events-none">
          <div
            className={`shrink-0 overflow-hidden rounded-md border ${
              isCompact ? 'h-10 w-10' : 'h-12 w-12'
            } ${isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-200 bg-gray-100'}`}
          >
            <img src={previewUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-center gap-1">
              <i className={`${icon} ${textXs} ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
              <span className={`truncate font-semibold ${textXs} ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {label}
              </span>
            </div>
            {active && (
              <div
                className="pointer-events-auto space-y-0.5"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <span className={`${textXs} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Opacity</span>
                  <span className={`${textXs} font-medium ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>
                    {Math.round((layerOpacity[layerKey as keyof LayerOpacityState] ?? 1) * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={layerOpacity[layerKey as keyof LayerOpacityState] ?? 1}
                  onChange={(e) =>
                    onOpacityChange(layerKey as keyof LayerOpacityState, parseFloat(e.target.value))
                  }
                  className={`h-1 w-full cursor-pointer appearance-none rounded-lg ${
                    isDark ? 'bg-gray-700' : 'bg-gray-200'
                  }`}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={isCompact ? 'mb-2' : 'mb-6'}>
      {/* Intro + How it works */}
      <div className={`mb-2 flex items-start justify-between ${gap}`}>
        <p className={`flex-1 leading-snug ${textXs} ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Quickly analyze large areas using existing data coverage or request drone services for detailed
          insights.
        </p>
        <button
          type="button"
          onClick={() => setShowHowItWorks((v) => !v)}
          className={`shrink-0 inline-flex items-center gap-1 rounded-md border px-2 py-1 font-medium transition-colors ${textXs} ${
            isDark
              ? 'border-emerald-600/60 text-emerald-300 hover:bg-emerald-900/30'
              : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
          }`}
        >
          <i className="ri-book-open-line" aria-hidden />
          <span>How it works</span>
        </button>
      </div>

      {showHowItWorks && (
        <div
          className={`mb-2 rounded-lg border ${pad} ${textXs} ${
            isDark
              ? 'border-emerald-700/50 bg-emerald-950/40 text-emerald-100'
              : 'border-emerald-200 bg-emerald-50 text-emerald-900'
          }`}
        >
          Choose <strong>With Drone Maps</strong> to view sample layers. When your field overlaps a
          mission, pick one crop check (like plant greenness), see it on the map, and download a simple
          summary. Choose <strong>Without Drone Maps</strong> to request new drone mapping or spraying.
        </div>
      )}

      {/* Sample data */}
      <div className={`mb-2 rounded-lg border ${pad} ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
        <div className={`mb-1.5 flex items-center gap-1 font-semibold ${textXs} ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <span>Sample data</span>
          <i className={`ri-information-line ${isDark ? 'text-gray-500' : 'text-gray-400'}`} aria-hidden />
        </div>
        <div className={`grid grid-cols-1 ${isCompact ? 'gap-1.5' : 'gap-2'}`}>
          {SAMPLE_OPTIONS.map((opt) => {
            const enabled = opt.key === 'isabela' ? canShowIsabela : canShowCavite;
            if (!enabled) return null;
            const selected = selectedSample === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSelectedSample(opt.key)}
                className={`flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors ${textXs} ${
                  selected
                    ? isDark
                      ? 'border-emerald-600 bg-emerald-900/30 text-white'
                      : 'border-emerald-400 bg-emerald-50 text-gray-900'
                    : isDark
                      ? 'border-gray-600 bg-gray-800 text-gray-200 hover:border-gray-500'
                      : 'border-gray-200 bg-gray-50 text-gray-800 hover:border-gray-300'
                }`}
              >
                <span
                  className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                    isDark ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  <i className={opt.icon} aria-hidden />
                </span>
                <span className="truncate font-medium">{opt.label}</span>
                <i className={`ri-arrow-down-s-line ml-auto shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </button>
            );
          })}
        </div>
        {selectedField && (
          <p className={`mt-1.5 ${textXs} ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Farm: <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{selectedField.farm_name}</span>
          </p>
        )}
      </div>

      {/* Status + mode toggle + request services */}
      <div
        className={`rounded-lg border-2 ${pad} ${
          isDark
            ? 'border-amber-600/40 bg-amber-950/25'
            : 'border-amber-200 bg-amber-50/80'
        }`}
      >
        {/* Drone map status */}
        <div className="mb-2 flex items-start gap-2">
          <span
            className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
              hasActiveDroneLayer
                ? isDark
                  ? 'bg-emerald-900/60 text-emerald-300'
                  : 'bg-emerald-100 text-emerald-600'
                : isDark
                  ? 'bg-amber-900/60 text-amber-300'
                  : 'bg-amber-100 text-amber-600'
            }`}
          >
            <i className={hasActiveDroneLayer ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} aria-hidden />
          </span>
          <div>
            <p className={`font-semibold ${textSm} ${isDark ? 'text-amber-100' : 'text-amber-950'}`}>
              Drone Map Status
            </p>
            <p className={`mt-0.5 leading-snug ${textXs} ${isDark ? 'text-amber-200/80' : 'text-amber-900/80'}`}>
              {hasActiveDroneLayer
                ? 'Drone map layers are active on the map. Adjust opacity or switch sample data above.'
                : 'No drone maps are currently available for this area. You can request drone services to generate high-resolution maps or apply treatments.'}
            </p>
          </div>
        </div>

        {/* With / Without toggle */}
        <div className={`mb-2 grid grid-cols-2 ${gap}`}>
          <button
            type="button"
            onClick={() => setDroneMapMode('with')}
            className={`relative rounded-lg border-2 px-2 py-2 text-center transition-all ${textXs} ${
              droneMapMode === 'with'
                ? isDark
                  ? 'border-emerald-500 bg-emerald-900/40 text-emerald-100'
                  : 'border-emerald-500 bg-white text-emerald-900 shadow-sm'
                : isDark
                  ? 'border-gray-600 bg-gray-800/80 text-gray-300 hover:border-gray-500'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            }`}
          >
            {droneMapMode === 'with' && (
              <i className="ri-checkbox-circle-fill absolute right-1.5 top-1.5 text-emerald-500 text-sm" aria-hidden />
            )}
            <i className="ri-map-2-line mb-1 block text-base" aria-hidden />
            <span className="font-semibold leading-tight">With Drone Maps</span>
          </button>
          <button
            type="button"
            onClick={() => setDroneMapMode('without')}
            className={`relative rounded-lg border-2 px-2 py-2 text-center transition-all ${textXs} ${
              droneMapMode === 'without'
                ? isDark
                  ? 'border-amber-500 bg-amber-900/30 text-amber-100'
                  : 'border-amber-400 bg-white text-amber-950 shadow-sm'
                : isDark
                  ? 'border-gray-600 bg-gray-800/80 text-gray-300 hover:border-gray-500'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            }`}
          >
            {droneMapMode === 'without' && (
              <i className="ri-checkbox-circle-fill absolute right-1.5 top-1.5 text-amber-500 text-sm" aria-hidden />
            )}
            <i className="ri-flight-takeoff-line mb-1 block text-base" aria-hidden />
            <span className="font-semibold leading-tight">Without Drone Maps</span>
          </button>
        </div>

        <div className={`mb-2 border-t border-dashed ${isDark ? 'border-amber-700/40' : 'border-amber-300/60'}`} />

        {/* With Drone Maps — layer picker */}
        {droneMapMode === 'with' && (
          <div className={`mb-2 space-y-1.5 ${textXs}`}>
            <p className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Tap a layer to show it on the map
            </p>
            {selectedSample === 'isabela' && canShowIsabela && (
              <>
                {canShow('isabelaPhilriceNDVI') &&
                  renderLayerCard(
                    activeMission,
                    'ndvi',
                    'isabelaPhilriceNDVI',
                    'Philrice Isabela_NDVI.tif',
                    'ri-leaf-line',
                    buildIsabelaPhilricePreviewUrl('ndvi'),
                    () => toggleNdvi(activeMission)
                  )}
                {canShow('isabelaPhilriceOrtho') &&
                  renderLayerCard(
                    activeMission,
                    'ortho',
                    'isabelaPhilriceOrtho',
                    'Philrice Isabela_Ortho.tif',
                    'ri-image-2-line',
                    buildIsabelaPhilricePreviewUrl('ortho'),
                    () => toggleOrtho(activeMission, 'isabelaPhilriceOrtho')
                  )}
              </>
            )}
            {selectedSample === 'cavite' && canShowCavite && (
              <>
                {canShow('caviteSilangNDVI') &&
                  renderLayerCard(
                    activeMission,
                    'ndvi',
                    'caviteSilangNDVI',
                    'SilangFarmLeadsOct_NDVI.tif',
                    'ri-leaf-line',
                    buildCaviteSilangPreviewUrl('ndvi'),
                    () => toggleNdvi(activeMission)
                  )}
                {canShow('caviteSilangOrtho') &&
                  renderLayerCard(
                    activeMission,
                    'ortho',
                    'caviteSilangOrtho',
                    'SilangFarmLeadsOct_Ortho.tif',
                    'ri-image-2-line',
                    buildCaviteSilangPreviewUrl('ortho'),
                    () => toggleOrtho(activeMission, 'caviteSilangOrtho')
                  )}
              </>
            )}

            <FarmDroneOrthoAnalysis
              isDark={isDark}
              isCompact={isCompact}
              mission={activeMission}
              fieldGeoJson={fieldGeoJson}
              farmName={selectedField?.farm_name}
              onShowIndexMap={(m, _indexKey, tileUrl) => onShowDroneOrthoIndexMap?.(m, tileUrl)}
              onClearIndexMap={onClearDroneOrthoIndexMap}
            />
          </div>
        )}

        {/* Without Drone Maps — request services */}
        {droneMapMode === 'without' && (
          <div>
            <div className="mb-2 flex items-start gap-2">
              <span
                className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                  isDark ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'
                }`}
              >
                <i className="ri-flight-takeoff-line" aria-hidden />
              </span>
              <div>
                <p className={`font-semibold ${textSm} ${isDark ? 'text-amber-100' : 'text-amber-950'}`}>
                  Request drone services
                </p>
                <p className={`mt-0.5 leading-snug ${textXs} ${isDark ? 'text-amber-200/75' : 'text-amber-900/75'}`}>
                  Activate a request for our drone team to capture maps or perform spraying services on
                  this area.
                </p>
              </div>
            </div>

            <div className={`grid grid-cols-1 ${isCompact ? 'gap-1.5' : 'gap-2'}`}>
              <button
                type="button"
                onClick={() => setDroneServiceType('mapping')}
                className={`drone-service-cta-btn flex w-full items-center gap-2 rounded-xl bg-[#008540] px-3 py-2.5 font-medium text-white shadow-sm transition-colors hover:bg-[#0f5830] ${textXs}`}
              >
                <i className="ri-drone-fill shrink-0 text-base text-white" aria-hidden />
                <span className="text-left leading-snug text-white">
                  <span className="block font-semibold text-white">Need Drone Mapping?</span>
                  <span className="font-normal text-white/95">Request high-res mapping</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setDroneServiceType('spraying')}
                className={`drone-service-cta-btn flex w-full items-center gap-2 rounded-xl bg-[#008540] px-3 py-2.5 font-medium text-white shadow-sm transition-colors hover:bg-[#0f5830] ${textXs}`}
              >
                <i className="ri-drone-fill shrink-0 text-base text-white" aria-hidden />
                <span className="text-left leading-snug text-white">
                  <span className="block font-semibold text-white">Need Drone Spraying?</span>
                  <span className="font-normal text-white/95">Request crop spraying</span>
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Footer note */}
        <div
          className={`mt-2 flex items-start gap-1.5 rounded-md ${pad} ${textXs} ${
            isDark ? 'bg-gray-800/60 text-gray-400' : 'bg-gray-100 text-gray-600'
          }`}
        >
          <i className="ri-information-line mt-0.5 shrink-0" aria-hidden />
          <span>Our team will review your request and contact you with next steps.</span>
        </div>
      </div>

      <DroneServiceRequestModal
        open={droneServiceType !== null}
        serviceType={droneServiceType}
        analysisMode="field-level"
        farmName={selectedField?.farm_name}
        onClose={() => setDroneServiceType(null)}
      />
    </div>
  );
}
